import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  LinearProgress,
  Grid,
  IconButton,
  Fab,
  Paper,
  Chip,
  Avatar,
  Snackbar,
  Alert
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Restaurant as RestaurantIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  LocalFireDepartment as CalorieIcon,
  Science as ScienceIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { calorieAIService } from '../../services/calorieAI/calorieAIService';
import FoodIdentification from './FoodIdentification';
import FoodDatabase from './FoodDatabase';
import GoalSetting from './GoalSetting';
import ProgressTracking from './ProgressTracking';
import HistoryManagement from './HistoryManagement';
import ModelTesting from './ModelTesting';

// 導入服務
const CalorieAI = () => {
  const [currentView, setCurrentView] = useState('main');
  const [todayData, setTodayData] = useState({
    totalCalories: 0,
    targetCalories: 2000,
    protein: 0,
    carbs: 0,
    fat: 0,
    meals: []
  });
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [weeklyData, setWeeklyData] = useState([]);

  // 餐型標籤轉換函數
  const getMealTypeLabel = (mealType) => {
    const mealTypeLabels = {
      breakfast: '早餐 🌅',
      lunch: '午餐 🍽️',
      dinner: '晚餐 🌆',
      latenight: '宵夜 🌃',
      snack: '點心 🍪'
    };
    return mealTypeLabels[mealType] || mealType;
  };

  useEffect(() => {
    // 使用 requestAnimationFrame 或 setTimeout 確保渲染不被阻塞
    const timer = setTimeout(() => {
      loadGoals();
      loadTodayData();
      loadWeeklyData();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const loadTodayData = async () => {
    try {
      // 不要在初始掛載時設定 loading 為 true，這會導致全螢幕的 Spinner 蓋住畫面
      // 只有在非初始加載時才顯示 loading
      if (todayData.meals.length > 0) {
        setLoading(true);
      }
      
      const today = new Date().toISOString().split('T')[0];
      const records = await calorieAIService.getFoodRecordsByDate(today);
      
      const totalCalories = records.reduce((sum, record) => sum + record.calories, 0);
      const protein = records.reduce((sum, record) => sum + record.protein, 0);
      const carbs = records.reduce((sum, record) => sum + record.carbs, 0);
      const fat = records.reduce((sum, record) => sum + record.fat, 0);

      setTodayData({
        totalCalories,
        targetCalories: goals?.daily_calories || 2000,
        protein,
        carbs,
        fat,
        meals: records
      });
    } catch (error) {
      console.error('載入今日數據失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGoals = async () => {
    try {
      const activeGoals = await calorieAIService.getActiveGoals();
      setGoals(activeGoals);
    } catch (error) {
      console.error('載入目標失敗:', error);
    }
  };

  // 獲取本週數據
  const loadWeeklyData = async () => {
    try {
      const weeklyStats = await calorieAIService.getWeeklyStats();
      setWeeklyData(weeklyStats);
    } catch (error) {
      console.error('載入週數據失敗:', error);
      setWeeklyData([]);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const handleFoodAdded = () => {
    loadTodayData();
    loadWeeklyData();
    setCurrentView('main');
  };

  // 週曆組件
  const WeeklyCalendar = () => {
    const today = new Date();
    const currentWeek = [];
    
    // 獲取本週日期（週日到週六）
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      currentWeek.push(date);
    }

    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    // 獲取日期的攝取狀態顏色
    const getStatusColor = (date, calories) => {
      // 如果沒有數據，返回灰色
      if (calories === 0) return '#9E9E9E'; // 灰色：無數據
      
      const targetCalories = goals?.daily_calories || 2000;
      const ratio = calories / targetCalories;
      
      if (ratio > 1.1) return '#FF5252'; // 紅色：超過目標
      if (ratio >= 0.8) return '#4CAF50'; // 綠色：接近目標
      return '#FFC107'; // 黃色：遠低於目標
    };

    // 獲取對應日期的卡路里數據
    const getCaloriesForDate = (date) => {
      const dateString = date.toISOString().split('T')[0];
      const dayData = weeklyData.find(day => day.date === dateString);
      return dayData ? dayData.calories : 0;
    };

    return (
      <Card sx={{ mb: 2, borderRadius: '16px' }}>
        <CardContent sx={{ py: 1.5 }}>
          <Grid container spacing={1}>
            {currentWeek.map((date, index) => {
              const calories = getCaloriesForDate(date);
              const statusColor = getStatusColor(date, calories);
              const isToday = date.toDateString() === today.toDateString();
              
              return (
                <Grid item xs key={index} sx={{ textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* 圓形指示器 - 改為外框樣式 */}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'transparent',
                        border: `2px solid ${statusColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        //mb: 0.5,
                        ...(isToday && {
                          boxShadow: `0 0 0 2px ${statusColor}40`,
                          borderWidth: '3px'
                        })
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#666', 
                          fontWeight: 'bold',
                          fontSize: '12px'
                        }}
                      >
                        {dayNames[index]}
                      </Typography>
                    </Box>
                    
                    {/* 日期數字 */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: isToday ? '#FF6B6B' : '#666',
                        fontWeight: isToday ? 'bold' : 'normal',
                        fontSize: '12px'
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderMainView = () => {
    const defaultCalories = 2000; // 預設值，但會優先使用用戶設定的目標
    const targetCalories = goals?.daily_calories || defaultCalories;
    const calorieProgress = (todayData.totalCalories / targetCalories) * 100;
    const remainingCalories = targetCalories - todayData.totalCalories;

    return (
      <Box sx={{ p: 2, pb: 10 }}>
        {/* 標題 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CalorieIcon sx={{ fontSize: 32, color: '#FF6B6B', mr: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
            Calorie AI 🤖
          </Typography>
        </Box>

        {/* 週曆組件 */}
        <WeeklyCalendar />

        {/* 快速操作按鈕 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<CameraIcon />}
              onClick={() => handleViewChange('camera')}
              sx={{
                py: 2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF5252, #FF7979)',
                }
              }}
            >
              拍照識別
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<RestaurantIcon />}
              onClick={() => handleViewChange('database')}
              sx={{
                py: 2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #43A047, #5CB85C)',
                }
              }}
            >
              手動輸入
            </Button>
          </Grid>
        </Grid>

        {/* 今日卡路里概覽 */}
        <Card sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
          color: 'white',
          borderRadius: '16px'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, opacity: 0.9 }}>
              今日卡路里攝取
            </Typography>
            
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
              {/* 背景圓圈 - 始終可見 */}
              <CircularProgress
                variant="determinate"
                value={100}
                size={120}
                thickness={6}
                sx={{
                  color: 'rgba(255, 255, 255, 0.2)',
                  position: 'absolute',
                }}
              />
              {/* 進度圓圈 */}
              <CircularProgress
                variant="determinate"
                value={Math.min(calorieProgress, 100)}
                size={120}
                thickness={6}
                sx={{
                  color: 'white',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {todayData.totalCalories}
                </Typography>
                <Typography variant="caption">
                  / {targetCalories}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 0.5 }}>
              剩餘: {remainingCalories > 0 ? remainingCalories : 0} 卡路里
            </Typography>
            
            <LinearProgress 
              variant="determinate" 
              value={Math.min(calorieProgress, 100)}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.3)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'white'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* 營養素分佈 */}
        <Card sx={{ mb: 3, borderRadius: '16px' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
               📊 營養分佈
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    {todayData.protein.toFixed(1)}g
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    / {goals?.daily_protein || 50}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    蛋白質
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                    {todayData.carbs.toFixed(1)}g
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    / {goals?.daily_carbs || 250}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    碳水化合物
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: '#FFC107', fontWeight: 'bold' }}>
                    {todayData.fat.toFixed(1)}g
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    / {goals?.daily_fat || 65}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    脂肪
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 今日餐點記錄 */}
        <Card sx={{ mb: 3, borderRadius: '16px' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
               🍽️ 今日餐點
            </Typography>
            {todayData.meals.length === 0 ? (
              <Typography color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                還沒有記錄任何餐點
              </Typography>
            ) : (
              todayData.meals.slice(0, 3).map((meal, index) => (
                <Box key={meal.id} sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  py: 1,
                  borderBottom: index < todayData.meals.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                      {meal.food_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {meal.quantity} {meal.unit} • {getMealTypeLabel(meal.meal_type)} • {meal.completion_percentage || 100}% 食用
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${meal.calories} 卡`}
                    size="small"
                    sx={{ backgroundColor: '#FFE0E0', color: '#FF6B6B' }}
                  />
                </Box>
              ))
            )}
            {todayData.meals.length > 3 && (
              <Button 
                fullWidth 
                variant="text" 
                onClick={() => handleViewChange('history')}
                sx={{ mt: 1, color: '#FF6B6B' }}
              >
                查看更多記錄
              </Button>
            )}
          </CardContent>
        </Card>

        {/* 功能導航 */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center', 
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => handleViewChange('progress')}
            >
              <TrendingUpIcon sx={{ fontSize: 32, color: '#FF6B6B', mb: 1 }} />
              <Typography variant="caption" display="block">
                進度追蹤
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center', 
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => handleViewChange('history')}
            >
              <HistoryIcon sx={{ fontSize: 32, color: '#4CAF50', mb: 1 }} />
              <Typography variant="caption" display="block">
                歷史記錄
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper 
              sx={{ 
                p: 2, 
                textAlign: 'center', 
                borderRadius: '12px',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
              onClick={() => handleViewChange('goals')}
            >
              <SettingsIcon sx={{ fontSize: 32, color: '#FFC107', mb: 1 }} />
              <Typography variant="caption" display="block">
                目標設定
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* 模型測試功能 */}
        <Card sx={{ mt: 3, borderRadius: '16px' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
              🧪 AI 模型測試
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              測試不同 AI 模型的食物識別性能和準確度
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ScienceIcon />}
              onClick={() => handleViewChange('modelTesting')}
              sx={{
                borderRadius: '12px',
                borderColor: '#9C27B0',
                color: '#9C27B0',
                '&:hover': {
                  borderColor: '#7B1FA2',
                  backgroundColor: 'rgba(156, 39, 176, 0.04)',
                }
              }}
            >
              開始模型測試
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'camera':
        return (
          <FoodIdentification 
            onBack={() => setCurrentView('main')}
            onFoodAdded={handleFoodAdded}
          />
        );
      case 'database':
        return (
          <FoodDatabase 
            onBack={() => setCurrentView('main')}
            onFoodAdded={handleFoodAdded}
          />
        );
      case 'goals':
        return (
          <GoalSetting 
            onBack={() => setCurrentView('main')}
            onGoalsUpdated={() => {
              loadGoals();
              loadTodayData();
              setCurrentView('main');
              setSnackbarMessage('設定保存成功');
              setSnackbarOpen(true);
            }}
          />
        );
      case 'progress':
        return (
          <ProgressTracking 
            onBack={() => setCurrentView('main')}
          />
        );
      case 'history':
        return (
          <HistoryManagement 
            onBack={() => setCurrentView('main')}
            onDataUpdated={loadTodayData}
          />
        );
      case 'modelTesting':
        return (
          <ModelTesting 
            onBack={() => setCurrentView('main')}
          />
        );
      default:
        return renderMainView();
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <CircularProgress sx={{ color: '#FF6B6B' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {renderCurrentView()}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CalorieAI;